import { DownOutlined } from "@payping/icons";
import { ValidEmail, ValidMobileNumber } from "@payping/utils/validation";
import { DashboardRoutes } from "@/router/constant";
import { Button, Drawer, LegacyForm, Space } from "@payping/web-components";
import type { UploadFile } from "@payping/web-components/components/upload/types";
import classNames from "classnames";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAsyncFn } from "react-use";

import styles from "@/assets/scss/dashCustomer/form.module.scss";
import { useAppTranslation } from "@/utils/useAppTranslation";
import HandlingCrispInMobile from "@/components/mobile/HandlingCrispInMobile";
import DrawerCloseButton from "@/components/Buttons/DrawerCloseButton";
import { CustomerFormProps, CustomerFormTypes } from "../types";
import BaseForm from "./Base";
import ExpandForm from "./Expand";

const CustomerForm: React.FC<CustomerFormProps> = (props: CustomerFormProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const { details, onOk, loading: loadingDetails, locationSearch, title } = props;
  const [phoneHelp, setPhoneHelp] = useState<string>();
  const [emailHelp, setEmailHelp] = useState<string>();
  const [expanded, setExpanded] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>(
    details?.userPhotoFileAddress
      ? [
          {
            name: "image",
            size: 0,
            type: "image/*",
            uid: "0",
            url: details.userPhotoFileAddress,
          },
        ]
      : [],
  );
  const navigate = useNavigate();
  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm<CustomerFormTypes>();

  const goToList = useCallback(() => {
    navigate({
      pathname: DashboardRoutes.myShop.customer.list.url,
      search: locationSearch,
    });
  }, [locationSearch, navigate]);

  useEffect(() => {
    if (details) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { userPhotoFileId, userPhotoFileAddress, ...data } = details;
      form.setFieldsValue({
        ...data,
        avatar: userPhotoFileAddress,
        tags: data.tags?.map((tag) => tag.id),
      });
    }
  }, [details, form]);

  const checkValidation = useCallback(
    (phone: string, email: string) => {
      let isValid = true;
      if (!email && !phone) {
        setPhoneHelp(t("error.phoneOrEmailRequired"));
        setEmailHelp(t("error.phoneOrEmailRequired"));
        isValid = false;
      } else if (email) {
        if (!ValidEmail(email)) {
          setEmailHelp(t("error.email"));
          isValid = false;
        } else {
          setEmailHelp("");
          setPhoneHelp("");
        }
      } else if (phone) {
        if (!ValidMobileNumber(phone)) {
          setPhoneHelp(t("error.phone"));
          isValid = false;
        } else {
          setEmailHelp("");
          setPhoneHelp("");
        }
      }
      return isValid;
    },
    [t],
  );

  const [{ loading: loadingSubmit }, handleSubmit] = useAsyncFn(
    async (values: CustomerFormTypes): Promise<void> => {
      const { avatar, tags: tagIds, ...data } = values;
      if (checkValidation(data.phone, data.email)) {
        await onOk({
          code: details?.code,
          ...data,
          tagIds,
          userPhotoFileId:
            typeof avatar === "string"
              ? avatar.split("/")[avatar.split("/").length - 1]
              : avatar?.fileList[0]?.response,
        });
        goToList();
      }
    },
    [checkValidation, details?.code, goToList, onOk],
  );

  const loading = useMemo(() => loadingDetails || loadingSubmit, [loadingDetails, loadingSubmit]);

  return (
    <Drawer
      open={isDrawerOpen}
      title={title}
      height={"100%"}
      closeIcon={false}
      placement={"bottom"}
      extra={<DrawerCloseButton setIsDrawerOpen={setIsDrawerOpen} />}
    >
      <HandlingCrispInMobile>
        <LegacyForm<CustomerFormTypes>
          canDeactivate={true}
          className={classNames(styles.form, "tw:w-full")}
          onValuesChange={(changedValues, values): void => {
            if (Object.keys(changedValues).find((value) => value === "phone" || value === "email")) {
              checkValidation(values.phone, values.email);
            }
          }}
          initialValues={{
            isBusiness: false,
            tags: [],
          }}
          onFinish={handleSubmit}
          form={form}
          layout={"vertical"}
        >
          <BaseForm
            phoneHelp={phoneHelp}
            emailHelp={emailHelp}
            loading={loading}
            fileList={fileList}
            setFileList={setFileList}
          />
          <ExpandForm className={expanded ? "" : "display-none"} loading={loading} form={form} />
          <LegacyForm.Item>
            <Button variant={"link"} onClick={(): void => setExpanded((prevState) => !prevState)}>
              <Space>
                {expanded ? t("customer.form.less") : t("customer.form.more")}
                <DownOutlined
                  width={"14px"}
                  style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "0.2s" }}
                />
              </Space>
            </Button>
          </LegacyForm.Item>
          <Space>
            <Button type={"primary"} htmlType={"submit"} loading={loading}>
              {t("save")}
            </Button>
            <Link to={{ pathname: DashboardRoutes.myShop.customer.list.url, search: locationSearch }}>
              <Button variant={"outlined"} color="primary">
                {t("return")}
              </Button>
            </Link>
          </Space>
        </LegacyForm>
      </HandlingCrispInMobile>
    </Drawer>
  );
};

export default CustomerForm;
